/**
 * The Quick Start REVIEW BODY — the proposal tree, shared by both screens.
 *
 * ── WHY THIS IS SHARED, WHEN SHARING IS NORMALLY A REFACTOR ────────────────────────────
 *
 * CLAUDE.md §1.2.1 says not to collapse several source screens into one parameterised
 * component. This is the opposite case: THE SOURCE ITSELF SHARES ONE FILE. Both
 * `views/roadmap-execution/quick-start.ejs` (the review screen) and
 * `quick-start-analysis.ejs` render their body with a single line —
 *
 *     <%- include('_quick-start-review') %>
 *
 * — so one component used twice IS the faithful clone, and writing a second body is the
 * deviation. That is exactly what had happened: Quick Start Analysis carried its own
 * invented `qa-proj` / `qa-opp` / `qa-proj-meta` markup, which is why its data looked
 * nothing like the source. The give-away was that `.qa-proj`, `.qa-chip`, `.qa-pi` and
 * `.qa-grp-hd` are styled in that view's <style> block and appear ZERO times in the
 * partial that actually renders the body — CSS written for markup the source never emits.
 *
 * Corrected 2026-08-17. The markup below is unchanged from the review screen it was lifted
 * out of; only its location moved.
 */
/* The 40 `qs2-*` classes this component renders live in QuickStartPreview.css, which only
   the PREVIEW screen imported — so opening the ANALYSIS screen rendered the whole proposal
   tree unstyled. A shared component must carry its own styles; QuickStartReviewBody.css
   has the full account. */
import './QuickStartPreview.css';
import './QuickStartReviewBody.css';
import { Fragment } from 'react';
import type React from 'react';

export type Dict = Record<string, unknown>;

export const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
export const n = (v: unknown) => Number(v ?? 0) || 0;

/** Spec 2.1 — identical to the Roadmap Definition Gantt, so one language across screens. */
export const PHASE_COLOUR: Record<string, string> = {
  Assessment_Planning: '#42a5f5',
  Design_Architecture: '#ab47bc',
  Development: '#66bb6a',
  Testing_Validation: '#ffa726',
  GoLive_Optimization: '#ef5350',
};
export const PHASE_LABEL: Record<string, string> = {
  Assessment_Planning: 'Assessment',
  Design_Architecture: 'Design',
  Development: 'Development',
  Testing_Validation: 'Testing',
  GoLive_Optimization: 'Go-Live',
};
/** Fixed group order; anything unrecognised is appended. Spec 4.10. */
export const PHASE_ORDER = Object.keys(PHASE_COLOUR);
/** Full labels, for the `phase_label` a re-phase has to rewrite alongside `phase_key`. */
export const PHASE_LABEL_FULL: Record<string, string> = {
  Assessment_Planning: 'Assessment & Planning',
  Design_Architecture: 'Design & Architecture',
  Development: 'Development',
  Testing_Validation: 'Testing & Validation',
  GoLive_Optimization: 'Go-Live & Optimization',
};
export const phaseColour = (k: string) => PHASE_COLOUR[k] ?? '#90a4ae';
export const phaseLabel = (k: string) => PHASE_LABEL[k] ?? k.replace(/_/g, ' ');

/** Spec Part 7 rule 6 — pluralise correctly, never "1 opportunitys". */
export const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

export const dateRange = (a: unknown, b: unknown) => {
  const x = s(a).slice(0, 10);
  const y = s(b).slice(0, 10);
  if (!x && !y) return '';
  return `${x || '?'} → ${y || '?'}`;
};

/** Spec 4.3 — metadata is NEVER bare text. Everything is a chip. */
export function Chip({
  kind,
  icon,
  children,
  from,
  title,
}: {
  kind?: string;
  icon?: string;
  children: React.ReactNode;
  /** The `· inherited` / `· derived` tail — an inferred value must not look stated. */
  from?: string;
  title?: string;
}) {
  return (
    <span className={`qs2-chip${kind ? ` ${kind}` : ''}`} title={title}>
      {icon ? <i className="material-icons">{icon}</i> : null}
      {children}
      {from ? <span className="qs2-from">· {from}</span> : null}
    </span>
  );
}

/**
 * The duplicate warning and its resolution control — design spec 4.12.
 *
 * A SEGMENTED CONTROL OF RADIOS, NOT A `<select>`. The spec is explicit and the reason is
 * concrete: a native select popup is translucent on Safari (vibrancy), and this screen's
 * coloured phase bars bled through the open menu. Inline radios have no popup, so nothing can
 * bleed. Do not substitute a dropdown.
 *
 * `Merge into #id` is the default WHERE A TARGET EXISTS — the common case is that the record
 * genuinely is the one already in the database, and merging adds only what is new. Where no
 * target could be identified the only honest defaults are Create new or Skip.
 */
export function DuplicateResolver({
  warn,
  level,
  value,
  onChange,
  disabled,
}: {
  warn: Dict;
  level: 'project' | 'opportunity';
  value: string | undefined;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  const idExists = Boolean(warn.idExists);
  const similar = warn.similar as { id?: string; pct?: number } | null;
  const target = idExists ? s(warn.code) : similar?.id ? s(similar.id) : null;
  const current = value ?? (target ? 'merge' : 'new');

  return (
    <>
      <div className="qs2-dup-note">
        ⚠️
        <div>
          {idExists ? (
            <>
              {level === 'project' ? 'Project' : 'Opportunity'} ID <b>{s(warn.code)}</b> already
              exists in the database.{' '}
            </>
          ) : null}
          {similar ? (
            <>
              Description is <b>{n(similar.pct)}% similar</b> to existing {level}{' '}
              <b>{s(similar.id)}</b> — likely a duplicate.
            </>
          ) : null}
        </div>
      </div>
      {!disabled ? (
        <div className="qs2-res">
          <span className="qs2-res-lbl">Duplicate — how to apply:</span>
          <span className="qs2-seg">
            {target ? (
              <label className="qs2-seg-opt" title={`Add only what's new to existing #${target}`}>
                <input
                  type="radio"
                  checked={current === 'merge'}
                  onChange={() => onChange('merge')}
                />
                <span>Merge into #{target}</span>
              </label>
            ) : null}
            <label
              className="qs2-seg-opt"
              title={`Create a separate record${idExists ? ' (new id)' : ''}`}
            >
              <input type="radio" checked={current === 'new'} onChange={() => onChange('new')} />
              <span>Create new</span>
            </label>
            <label className="qs2-seg-opt" title="Don't create it">
              <input type="radio" checked={current === 'skip'} onChange={() => onChange('skip')} />
              <span>Skip</span>
            </label>
          </span>
        </div>
      ) : null}
    </>
  );
}

export function Methodology({ value }: { value: unknown }) {
  const v = s(value).toLowerCase();
  if (!v) return null;
  const agile = v === 'agile';
  return <Chip kind={`meth ${agile ? 'ag' : 'wf'}`}>{agile ? 'Agile' : 'Waterfall'}</Chip>;
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * Selection — spec 5.3.
 *
 * Addressed entirely through composite keys, never element ids, so the rules can be reasoned
 * about without touching the DOM:
 *
 *   program      pid
 *   opportunity  pid|oid
 *   work item    pid|oid|did
 *   team member  team:pid|tref
 *
 * The cascade: checking a PROGRAM sets every descendant; checking an OPPORTUNITY checks its
 * work items AND back-fills its program; checking a WORK ITEM back-fills both parents.
 * UNCHECKING A CHILD NEVER UNCHECKS ITS PARENT — only downward propagation clears.
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/** The drop-zone identity for a phase group. Pure, and used only by the tree. */
export const dropKey = (pid: string, oid: string | null, pk: string) => `${pid}|${oid ?? ''}|${pk}`;

export const keyProg = (pid: string) => pid;
export const keyOpp = (pid: string, oid: string) => `${pid}|${oid}`;
export const keyDel = (pid: string, oid: string, did: string) => `${pid}|${oid}|${did}`;
export const keyTeam = (pid: string, tref: string) => `team:${pid}|${tref}`;
/* Programme-level work items hang off the PROGRAM, not an opportunity — they are created once
   with `opportunity_id` NULL — so their key carries no opportunity segment. */
export const keyGlobal = (pid: string, gi: string) => `global:${pid}|${gi}`;

/**
 * §19.2 — WHICH RECORDS ARE LOCKED. A RUN IS NEVER LOCKED AS A WHOLE.
 *
 * The screen used to gate every control on the run's status. That is §19.1's failure in a
 * different spelling: a flag derived from *"did this run generate anything?"* applied to
 * controls whose correctness depends on *"was THIS record generated?"*. A run that merely
 * merged into projects that already existed took the whole review read-only — drag and drop
 * included, which was the loudest part of the complaint — while nothing showed a Generated
 * badge, because the badge and the lock were reading two different queries.
 *
 * Only a project or opportunity that genuinely exists in the database is locked, and only
 * itself. A new initiative under a project generated earlier stays selectable.
 *
 * Exported so the lock the tests count is the lock the screen renders — a test written
 * against a re-typed copy of this rule would pass while the screen was wrong.
 *
 * The id a record was created under is matched against BOTH `code` and `ref`, because a
 * merge writes the proposal's own `code` while a fresh insert may mint a free one.
 */

/**
 * §19.2 — WHICH RECORDS ARE LOCKED. A RUN IS NEVER LOCKED AS A WHOLE.
 *
 * Exported so the lock the tests count is the lock the screen renders. The id a record was
 * created under is matched against BOTH `code` and `ref`, because a merge writes the
 * proposal's own `code` while a fresh insert may mint a free one.
 */
export function computeLocks(
  programs: Dict[],
  createdIds?: { project?: string[]; opportunity?: string[] },
): { genProg: Set<string>; genOpp: Set<string> } {
  const madeProjects = new Set(createdIds?.project ?? []);
  const madeOpps = new Set(createdIds?.opportunity ?? []);
  const genProg = new Set<string>();
  const genOpp = new Set<string>();
  for (const pg of programs ?? []) {
    const pid = s(pg.ref ?? pg.code);
    if (madeProjects.has(s(pg.code)) || madeProjects.has(pid)) genProg.add(keyProg(pid));
    for (const o of ((pg.opportunities as Dict[]) ?? [])) {
      const oid = s(o.ref ?? o.code);
      if (madeOpps.has(s(o.code)) || madeOpps.has(oid)) genOpp.add(keyOpp(pid, oid));
    }
  }
  return { genProg, genOpp };
}

/* ── The body itself ─────────────────────────────────────────────────────────────────── */

export interface QuickStartReviewBodyProps {
  programs: Dict[];
  /** The whole proposal — the tree reads `roles` off it for the RACI legend. */
  proposal: Dict | null;
  /** Which programmes and opportunities survive the current filter. */
  visible: { progs: Set<string>; opps: Set<string> };
  /** §19.2 — the records that genuinely exist, each locking only itself. */
  genProg: Set<string>;
  genOpp: Set<string>;
  isOn: (k: string) => boolean;
  setMany: (keys: string[], on: boolean) => void;
  toggleProgram: (pg: Dict, on: boolean) => void;
  toggleOpp: (pg: Dict, o: Dict, on: boolean) => void;
  toggleDel: (pg: Dict, o: Dict, idx: number, on: boolean) => void;
  dupWarn: { programs: Record<string, Dict>; opportunities: Record<string, Dict> };
  resolutions: Record<string, string>;
  setResolutions: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  /* Drag state. Kept as props rather than re-implemented, so a work item dragged on either
     screen behaves identically — the source has one implementation because it has one file. */
  /** Which drop zone the pointer is currently over, and where in it — `hot`/`ghot` are
      derived from this inside the tree, exactly as they were when it lived inline. */
  dropHint: string | null;
  dropBefore: number | null;
  onItemDragStart: (e: React.DragEvent, pid: string, oid: string | null, index: number) => void;
  onItemDragEnd: () => void;
  onPhaseDragOver: (e: React.DragEvent, pid: string, oid: string | null, pk: string) => void;
  onPhaseDrop: (e: React.DragEvent, pid: string, oid: string | null, pk: string) => void | Promise<void>;
}

export function QuickStartReviewBody({
  programs, proposal, visible, genProg, genOpp, isOn, setMany,
  toggleProgram, toggleOpp, toggleDel,
  dupWarn, resolutions, setResolutions,
  dropHint, dropBefore,
  onItemDragStart, onItemDragEnd, onPhaseDragOver, onPhaseDrop,
}: QuickStartReviewBodyProps) {
  return (
    <>
            {programs.map((pg, pi) => {
              const pid = s(pg.ref || pg.code || pi);
              /* Filtered out entirely — see `visible`. Returning null rather than hiding with
                 CSS keeps the thousands of work items under it out of the DOM as well, which is
                 what makes filtering a 90-project intake feel instant. */
              if (!visible.progs.has(pid)) return null;
              /* §19.2 — this project's own lock. Not the run's. */
              const pgen = genProg.has(keyProg(pid));
              const opps = (pg.opportunities as Dict[]) ?? [];
              const team = (pg.team as Dict[]) ?? [];
              const raci = (pg.raci as Dict[]) ?? [];
              const roles = (proposal?.roles as Dict[]) ?? [];

              /* Programme-level work items, grouped by phase in the canonical order with any
                 unrecognised phase appended — the same rule the per-opportunity groups use, so
                 the two lists read identically. */
              const globals = (pg.global_deliverables as Dict[]) ?? [];
              const globalsByPhase = new Map<string, { d: Dict; gi: number }[]>();
              globals.forEach((d, gi) => {
                const k = s(d.phase_key ?? d.phase) || 'Assessment_Planning';
                if (!globalsByPhase.has(k)) globalsByPhase.set(k, []);
                globalsByPhase.get(k)!.push({ d, gi });
              });
              /* Every phase is a drop target while a programme-level item is being dragged,
                 occupied or not — otherwise an item could never be moved INTO an empty phase.
                 §19.2 — DRAG AND DROP IS NEVER GATED. Dragging a work item to another
                 phase edits the PROPOSAL, not the generated records, so there is no reason
                 to forbid it after a generation. Forbidding it was the loudest part of the
                 complaint. */
              const globalDragPhaseKeys = [
                ...PHASE_ORDER,
                ...[...globalsByPhase.keys()].filter((k) => !PHASE_ORDER.includes(k)),
              ];

              return (
                <Fragment key={pid}>
                  {/* `_quick-start-review.ejs:23` — "Project <code>" rule between cards, so a
                      long intake reads as separate projects rather than one wall. */}
                  {pi ? <div className="qs2-prog-sep">Project {s(pg.code) || pid}</div> : null}
                <div className="qs2-prog">
                  {/* The whole header is the click target — a <label> wrapping its checkbox. */}
                  <label className="qs2-prog-hd">
                    <input
                      type="checkbox"
                      className="qs2-cb"
                      disabled={pgen}
                      checked={isOn(keyProg(pid))}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleProgram(pg, e.target.checked)}
                    />
                    {/* The source's badge, not a folder icon: `_quick-start-review.ejs:29`
                        renders <span class="qs-pi p" title="Project">P</span>. */}
                    <span className="qs2-pi p" title="Project">
                      P
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="qs2-row">
                        {pg.code ? <Chip kind="code">{s(pg.code)}</Chip> : null}
                        <span className="qs2-prog-name">{s(pg.name)}</span>
                        {pgen ? (
                          <Chip kind="gen">✓ Generated</Chip>
                        ) : (
                          <Chip kind="status">{s(pg.status) || 'Active'}</Chip>
                        )}
                        {pg.owner ? (
                          <Chip kind="owner">Owner:&nbsp;{s(pg.owner)}</Chip>
                        ) : null}
                        {pg.sponsor ? (
                          <Chip kind="sponsor">Sponsor:&nbsp;{s(pg.sponsor)}</Chip>
                        ) : null}
                        {pg.name_inferred ? (
                          <Chip
                            kind="warn"
                            title="The project name was not stated in the document and was inferred — rename it before generating."
                          >
                            NAME INFERRED
                          </Chip>
                        ) : null}
                      </div>
                      {pg.description ? (
                        <div className="qs2-mini" style={{ marginTop: 6 }}>
                          {s(pg.description)}
                        </div>
                      ) : null}
                      <div className="qs2-row">
                        {pg.start_date || pg.end_date ? (
                          <Chip
                            kind="date"
                            from={pg.dates_derived ? 'derived' : undefined}
                            title={
                              pg.dates_derived
                                ? 'Not stated on the project — derived from the span of its opportunities.'
                                : undefined
                            }
                          >
                            {dateRange(pg.start_date, pg.end_date)}
                          </Chip>
                        ) : null}
                        <Methodology value={pg.methodology} />
                        <Chip kind="count">
                          {opps.length} {plural(opps.length, 'opportunity', 'opportunities')}
                        </Chip>
                        {pg.category ? <Chip>{s(pg.category)}</Chip> : null}
                        <span className="qs2-mini">→ Project Management</span>
                        {dupWarn.programs[pid] ? <Chip kind="dup">⚠ Possible duplicate</Chip> : null}
                      </div>
                    </div>
                  </label>

                  {dupWarn.programs[pid] ? (
                    <DuplicateResolver
                      warn={dupWarn.programs[pid]!}
                      level="project"
                      disabled={pgen}
                      value={resolutions[s(pg.ref)]}
                      onChange={(v) =>
                        setResolutions((prev) => ({ ...prev, [s(pg.ref)]: v }))
                      }
                    />
                  ) : null}

                  {/*
                    ALWAYS RENDERED — no expand/collapse on a project or an opportunity.

                    The source has none: `.qs-prog-body` and `.qs-opp-detail` are plain divs
                    (`views/roadmap-execution/quick-start.ejs:579, 638`), and the ONLY collapsible
                    thing on the screen is the run history. Opening a review therefore shows every
                    project, every opportunity and every work item at once.

                    A collapse was invented here, and it hid the work items behind two clicks per
                    opportunity — so the screen appeared to have produced none. The screen's job is
                    triage across ~170 opportunities and ~1,700 work items (design spec Part 1);
                    that only works if they are all on screen to be scanned.
                  */}
                  <div className="qs2-prog-body">
                      {/*
                        PROGRAMME-LEVEL WORK ITEMS — cloned from the source view's `global_deliverables`
                        block (`views/roadmap-execution/quick-start.ejs:556`), including its label
                        wording and its phase grouping.

                        These are created ONCE for the project with no opportunity, so they sit
                        ABOVE the opportunity list rather than inside any one of them — which is
                        also the only honest place to put them: nesting a shared artifact under one
                        opportunity would imply it belongs to that opportunity.
                      */}
                      {globals.length ? (
                        <>
                          <p className="qs2-lbl">
                            Programme-level work items{' '}
                            <span className="qs2-dest">
                              → Work Item Definition (<strong>Global</strong>)
                            </span>{' '}
                            · created <strong>once</strong> for this project, not per opportunity
                          </p>
                          <div style={{ marginBottom: 12 }}>
                            {/*
                              PROGRAMME-LEVEL ITEMS ARE DRAGGABLE TOO — the source marks them
                              `draggable="true"` with the same handlers, and tells the two apart
                              by the ABSENCE of `data-oid` (`quick-start.ejs:584`). Passing
                              `oid = null` here is that same distinction, and the reorder endpoint
                              already accepts it: with no oid it rewrites `global_deliverables`
                              instead of one opportunity's list.

                              Without this the standing 18 could not be re-phased at all, which
                              is exactly half the work items on a programme.
                            */}
                            {globalDragPhaseKeys.map((pk) => {
                              const grows = globalsByPhase.get(pk) ?? [];
                              const ghot = dropHint === dropKey(pid, null, pk);
                              return (
                              <div
                                className={`qs2-delgrp${grows.length ? '' : ' qs2-delgrp-empty'}${
                                  ghot ? ' qs2-drop-ok' : ''
                                }`}
                                key={pk}
                                onDragOver={(e) => onPhaseDragOver(e, pid, null, pk)}
                                onDrop={(e) => void onPhaseDrop(e, pid, null, pk)}
                              >
                                <div className="qs2-delgrp-hd">
                                  <span
                                    className="qs2-dot"
                                    style={{ background: phaseColour(pk) }}
                                  />
                                  {phaseLabel(pk)}
                                </div>
                                {grows.map(({ d, gi }) => {
                                  const activity =
                                    s(d.work_item_type).toLowerCase() === 'activity';
                                  return (
                                    <Fragment key={gi}>
                                    {ghot && dropBefore === gi ? (
                                      <div className="qs2-ins" />
                                    ) : null}
                                    <label
                                      className="qs2-del"
                                      data-qsidx={gi}
                                      draggable
                                      onDragStart={(e) => onItemDragStart(e, pid, null, gi)}
                                      onDragEnd={onItemDragEnd}
                                    >
                                      <input
                                        type="checkbox"
                                        className="qs2-cb"
                                        disabled={pgen}
                                        checked={isOn(keyGlobal(pid, String(gi)))}
                                        onChange={(e) =>
                                          setMany(
                                            e.target.checked
                                              ? [keyGlobal(pid, String(gi)), keyProg(pid)]
                                              : [keyGlobal(pid, String(gi))],
                                            e.target.checked,
                                          )
                                        }
                                      />
                                      <span
                                        className={`qs2-wit ${activity ? 'a' : 'd'}`}
                                        title={activity ? 'Activity' : 'Deliverable'}
                                      >
                                        {activity ? 'A' : 'D'}
                                      </span>
                                      <span className="qs2-del-name">{s(d.name)}</span>
                                      {/* A custom global item came from the analyst's reference
                                          file, not the catalogue — say so, or it reads as
                                          something the AI invented. */}
                                      {d.is_custom ? (
                                        <span className="qs2-mini" style={{ marginLeft: 6 }}>
                                          from your reference file
                                        </span>
                                      ) : null}
                                      <i
                                        className="material-icons qs2-grip"
                                        title="Drag to another phase"
                                      >
                                        drag_indicator
                                      </i>
                                    </label>
                                    </Fragment>
                                  );
                                })}
                                {/* Pointer below every row — the line marks the end. */}
                                {ghot && dropBefore === null ? (
                                  <div className="qs2-ins" />
                                ) : null}
                              </div>
                              );
                            })}
                          </div>
                        </>
                      ) : null}

                      {team.length ? (
                        <div className="qs2-team">
                          <div className="qs2-team-hd">
                            👥 Team members from the deck
                            <span className="qs2-mini">→ Project team</span>
                          </div>
                          {team.map((t, ti) => (
                            <label className="qs2-team-row" key={ti}>
                              <input
                                type="checkbox"
                                className="qs2-cb"
                                disabled={pgen}
                                checked={isOn(keyTeam(pid, s(t.ref || t.name)))}
                                onChange={(e) =>
                                  setMany([keyTeam(pid, s(t.ref || t.name))], e.target.checked)
                                }
                              />
                              <span className="qs2-team-name">{s(t.name)}</span>
                              {t.role ? <Chip>{s(t.role)}</Chip> : null}
                              {t.email ? <span className="qs2-mini">{s(t.email)}</span> : null}
                            </label>
                          ))}
                        </div>
                      ) : null}

                      <p className="qs2-lbl">
                        Opportunities{' '}
                        <span className="qs2-dest">
                          → All Opportunities · Pathway (A or B) → chosen by you on Delivery
                          Pathway
                        </span>
                      </p>

                      {/* `_quick-start-review.ejs:95` wraps the initiatives so they sit
                          visibly INSIDE their project — indent, a rule down the group, and a
                          stub to each card. Ours rendered them as a flat list. */}
                      <div className="qs2-opps-wrap">
                      {opps.map((o, oi) => {
                        const oid = s(o.ref || o.code || oi);
                        const okey = keyOpp(pid, oid);
                        if (!visible.opps.has(okey)) return null;
                        /* §19.2 — this initiative's own lock. A new initiative under a
                           project generated earlier is NOT locked by that project. */
                        const ogen = genOpp.has(okey);
                        const phases =
                          ((pg.phases_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ?? [];
                        const dels =
                          ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ??
                          [];

                        /* Group work items by phase, fixed order, unknown phases appended. */
                        const byPhase = new Map<string, { d: Dict; i: number }[]>();
                        dels.forEach((d, i) => {
                          /* `phase_key` since the catalogue change; `phase` is the pre-catalogue
                             shape, kept so a proposal analysed before it still groups rather
                             than collapsing every item into Assessment & Planning. */
                          const k = s(d.phase_key ?? d.phase) || 'Assessment_Planning';
                          if (!byPhase.has(k)) byPhase.set(k, []);
                          byPhase.get(k)!.push({ d, i });
                        });
                        /* Every phase is a drop target while dragging, occupied or not — an
                           empty one renders as a slim strip. §19.2: never gated on the run
                           having generated, because a drag edits the proposal, not the
                           records that were created from it. */
                        const dragPhaseKeys = [
                          ...PHASE_ORDER,
                          ...[...byPhase.keys()].filter((k) => !PHASE_ORDER.includes(k)),
                        ];

                        return (
                          <div className="qs2-opp" key={oid}>
                            <label className="qs2-opp-hd">
                              <input
                                type="checkbox"
                                className="qs2-cb"
                                disabled={ogen}
                                checked={isOn(okey)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => toggleOpp(pg, o, e.target.checked)}
                              />
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="qs2-row">
                                  {/* `_quick-start-review.ejs:102` —
                                      <span class="qs-pi i" title="Initiative (opportunity)">I</span>.
                                      This row previously carried NO indicator, so a reader
                                      could not tell an initiative from a project at all. */}
                                  <span className="qs2-pi i" title="Initiative (opportunity)">
                                    I
                                  </span>
                                  {o.code ? <Chip kind="code">{s(o.code)}</Chip> : null}
                                  <span className="qs2-opp-name">{s(o.name)}</span>
                                  {ogen ? (
                                    <Chip kind="gen">✓ Generated</Chip>
                                  ) : (
                                    <Chip kind="status">{s(o.status) || 'Approved'}</Chip>
                                  )}
                                  {o.owner ? (
                                    <Chip
                                      kind="owner"
                                      from={o.owner_inherited ? 'inherited' : undefined}
                                      title={
                                        o.owner_inherited
                                          ? 'Not named on this opportunity — inherited from the project owner.'
                                          : undefined
                                      }
                                    >
                                      Owner:&nbsp;{s(o.owner)}
                                    </Chip>
                                  ) : null}
                                  {o.sponsor ? (
                                    <Chip
                                      kind="sponsor"
                                      from={o.sponsor_inherited ? 'inherited' : undefined}
                                      title={
                                        o.sponsor_inherited
                                          ? 'Not named on this opportunity — inherited from the project sponsor.'
                                          : undefined
                                      }
                                    >
                                      Sponsor:&nbsp;{s(o.sponsor)}
                                    </Chip>
                                  ) : null}
                                </div>
                                {o.description ? (
                                  <div className="qs2-mini" style={{ marginTop: 6 }}>
                                    {s(o.description)}
                                  </div>
                                ) : null}
                                <div className="qs2-row">
                                  {o.start_date || o.end_date ? (
                                    <Chip kind="date">
                                      {dateRange(o.start_date, o.end_date)}
                                    </Chip>
                                  ) : null}
                                  <Methodology value={o.methodology} />
                                  {dels.length ? (
                                    <Chip kind="count">
                                      {dels.length}{' '}
                                      {plural(dels.length, 'work item', 'work items')}
                                    </Chip>
                                  ) : null}
                                  {o.category ? <Chip>{s(o.category)}</Chip> : null}
                                  {dupWarn.opportunities[s(o.ref)] ? (
                                    <Chip kind="dup">⚠ Possible duplicate</Chip>
                                  ) : null}
                                </div>
                              </div>
                            </label>

                            {dupWarn.opportunities[s(o.ref)] ? (
                              <DuplicateResolver
                                warn={dupWarn.opportunities[s(o.ref)]!}
                                level="opportunity"
                                disabled={ogen}
                                value={resolutions[s(o.ref)]}
                                onChange={(v) =>
                                  setResolutions((prev) => ({ ...prev, [s(o.ref)]: v }))
                                }
                              />
                            ) : null}

                            <div className="qs2-opp-detail">
                                {phases.length ? (
                                  <div className="qs2-phaserow">
                                    {phases.map((ph, pi2) => (
                                      <span
                                        className="qs2-ph-box"
                                        key={pi2}
                                        style={{ background: phaseColour(s(ph.key)) }}
                                        title={dateRange(ph.start, ph.end)}
                                      >
                                        {phaseLabel(s(ph.key))} · {dateRange(ph.start, ph.end)}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="qs2-mini">
                                    No dates — phases proposed once start/end are set.
                                  </div>
                                )}

                                {/* ALL FIVE PHASES are rendered, not just the occupied ones —
                                    spec 6.5. An empty phase is a slim labelled strip so there
                                    is always somewhere to drop; without it a work item could
                                    never be moved INTO a phase that happens to be empty, which
                                    is exactly when you most want to. */}
                                {dragPhaseKeys.map((pk) => {
                                  const rows = byPhase.get(pk) ?? [];
                                  const hot = dropHint === dropKey(pid, oid, pk);
                                  return (
                                  <div
                                    className={`qs2-delgrp${rows.length ? '' : ' qs2-delgrp-empty'}${
                                      hot ? ' qs2-drop-ok' : ''
                                    }`}
                                    key={pk}
                                    onDragOver={(e) => onPhaseDragOver(e, pid, oid, pk)}
                                    onDrop={(e) => void onPhaseDrop(e, pid, oid, pk)}
                                  >
                                    <div className="qs2-delgrp-hd">
                                      <span
                                        className="qs2-dot"
                                        style={{ background: phaseColour(pk) }}
                                      />
                                      {phaseLabel(pk)}
                                    </div>
                                    {rows.map(({ d, i }) => {
                                      const activity =
                                        s(d.work_item_type).toLowerCase() === 'activity';
                                      return (
                                        <Fragment key={i}>
                                        {hot && dropBefore === i ? (
                                          <div className="qs2-ins" />
                                        ) : null}
                                        <label
                                          className="qs2-del"
                                          data-qsidx={i}
                                          draggable
                                          onDragStart={(e) => onItemDragStart(e, pid, oid, i)}
                                          onDragEnd={onItemDragEnd}
                                        >
                                          <input
                                            type="checkbox"
                                            className="qs2-cb"
                                            disabled={ogen}
                                            checked={isOn(keyDel(pid, oid, String(i)))}
                                            onChange={(e) =>
                                              toggleDel(pg, o, i, e.target.checked)
                                            }
                                          />
                                          <span
                                            className={`qs2-wit ${activity ? 'a' : 'd'}`}
                                            title={activity ? 'Activity' : 'Deliverable'}
                                          >
                                            {activity ? 'A' : 'D'}
                                          </span>
                                          <span className="qs2-del-name">{s(d.name)}</span>
                                          {/* A phase a REVIEWER chose, not the catalogue —
                                              worth saying, because it is the one placement no
                                              re-analysis would reproduce. */}
                                          {d.phase_overridden ? (
                                            <span className="qs2-moved" title="Moved here by you">
                                              moved
                                            </span>
                                          ) : null}
                                          <i className="material-icons qs2-grip" title="Drag to another phase">
                                            drag_indicator
                                          </i>
                                        </label>
                                        </Fragment>
                                      );
                                    })}
                                    {hot && dropBefore === null ? (
                                      <div className="qs2-ins" />
                                    ) : null}
                                  </div>
                                  );
                                })
                                }
                              </div>
                          </div>
                        );
                      })}
                      </div>

                      {raci.length ? (
                        <>
                          <p className="qs2-lbl">
                            RACI <span className="qs2-dest">→ RACI Definition</span>
                          </p>
                          <div className="qs2-tblwrap">
                            <table className="qs2-tbl">
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left' }}>Phase</th>
                                  {roles.map((r, i) => (
                                    <th key={i}>{s(r.code || r.label)}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {raci.map((row, ri) => {
                                  const m = (row.matrix as Record<string, string>) ?? {};
                                  return (
                                    <tr key={ri}>
                                      <td className="ph">{s(row.phase_label ?? row.phase)}</td>
                                      {roles.map((r, ci) => {
                                        const v = s(m[s(r.code)]);
                                        const cls =
                                          v === 'A' ? 'raci-a' : v === 'R' ? 'raci-r' : 'raci-o';
                                        return (
                                          <td className={cls} key={ci}>
                                            {v || '—'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <p className="qs2-mini" style={{ marginTop: 6 }}>
                            R = Responsible · A = Accountable · C = Consulted · I = Informed
                          </p>
                        </>
                      ) : null}
                    </div>
                </div>
                </Fragment>
              );
            })}
    </>
  );
}
